/**
 * PickingPathView - Main view for product picking with map and step list
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Refresh as RefreshIcon,
  Map as MapIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory2 as ProductIcon,
  ViewInAr as View3DIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { calculatePickingPath } from '../../services/locationApi';
import {
  PickingPath,
  PickingStep,
  PickingStrategy,
  TransportMode,
  getHeightAccessibilityColor,
} from './pathTypes';
import RackDetailView from './RackDetailView';

interface PickingItem {
  productId: number;
  productCode: string;
  productName: string;
  quantity: number;
}

interface PickingPathViewProps {
  warehouseId: number;
  documentId?: number;
  items?: PickingItem[];
  onStepHighlight?: (step: PickingStep | null) => void;
  onPathCalculated?: (path: PickingPath) => void;
  onClose?: () => void;
}

const PickingPathView: React.FC<PickingPathViewProps> = ({
  warehouseId,
  documentId,
  items,
  onStepHighlight,
  onPathCalculated,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [strategy, setStrategy] = useState<PickingStrategy>('shortest');
  const [transportMode, setTransportMode] = useState<TransportMode>('forklift');
  const [pickingPath, setPickingPath] = useState<PickingPath | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkedSteps, setCheckedSteps] = useState<Set<number>>(new Set());
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [rackDetailOpen, setRackDetailOpen] = useState(false);
  const [selectedRackId, setSelectedRackId] = useState<number | null>(null);
  const [highlightedShelfLevel, setHighlightedShelfLevel] = useState<number | undefined>();

  const handleCalculatePath = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCheckedSteps(new Set());

    try {
      const path = await calculatePickingPath(warehouseId, {
        documentId,
        items: items?.map(i => ({ productId: i.productId, quantity: i.quantity })),
        strategy,
        transportMode,
      });

      setPickingPath(path);
      onPathCalculated?.(path);
    } catch (err: any) {
      console.error('Error calculating picking path:', err);
      setError(err.response?.data?.error || t('documents.pickingPath.calculatePathError'));
    } finally {
      setLoading(false);
    }
  }, [warehouseId, documentId, items, strategy, transportMode, onPathCalculated, t]);

  const handleStepToggle = (stepOrder: number) => {
    const newChecked = new Set(checkedSteps);
    if (newChecked.has(stepOrder)) {
      newChecked.delete(stepOrder);
    } else {
      newChecked.add(stepOrder);
    }
    setCheckedSteps(newChecked);
  };

  const handleStepExpand = (stepOrder: number) => {
    setExpandedStep(expandedStep === stepOrder ? null : stepOrder);
  };

  const handleStepHover = (step: PickingStep | null) => {
    onStepHighlight?.(step);
  };

  const handleOpenRackDetail = (step: PickingStep) => {
    const rackId = step.rack?.id || step.location.id;
    setSelectedRackId(rackId);
    setHighlightedShelfLevel(step.location.shelfLevel);
    setRackDetailOpen(true);
  };

  const completedCount = checkedSteps.size;
  const totalSteps = pickingPath?.steps.length || 0;
  const progress = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  const renderStepItem = (step: PickingStep) => {
    const isChecked = checkedSteps.has(step.order);
    const isExpanded = expandedStep === step.order;
    const accessibilityColor = getHeightAccessibilityColor(step.heightAccessibility);

    return (
      <React.Fragment key={step.order}>
        <ListItem
          sx={{
            bgcolor: isChecked ? 'action.selected' : 'transparent',
            borderLeft: `4px solid ${accessibilityColor}`,
            mb: 0.5,
            borderRadius: 1,
            cursor: 'pointer',
          }}
          onMouseEnter={() => handleStepHover(step)}
          onMouseLeave={() => handleStepHover(null)}
        >
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={isChecked}
              onChange={() => handleStepToggle(step.order)}
              icon={<UncheckedIcon />}
              checkedIcon={<CheckIcon color="success" />}
            />
          </ListItemIcon>

          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={step.order}
                  size="small"
                  color="primary"
                  sx={{ minWidth: 28 }}
                />
                <Typography
                  variant="body1"
                  sx={{ textDecoration: isChecked ? 'line-through' : 'none' }}
                >
                  {step.product.name}
                </Typography>
              </Box>
            }
            secondary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {step.rack?.code || step.location.code}
                  {step.location.shelfLevel > 0 && ` / P${step.location.shelfLevel}`}
                </Typography>
                <Chip
                  label={`${step.quantityToPick} ${t('documents.pickingPath.unitPcs')}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            }
          />

          <ListItemSecondaryAction>
            <Tooltip title={t('documents.pickingPath.rackDetails')}>
              <IconButton
                size="small"
                onClick={() => handleOpenRackDetail(step)}
                sx={{ mr: 1 }}
              >
                <MapIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => handleStepExpand(step.order)}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>

        <Collapse in={isExpanded}>
          <Box sx={{ pl: 9, pr: 2, pb: 1 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('documents.pickingPath.productCode')}</Typography>
                  <Typography variant="body2">{step.product.code}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('documents.pickingPath.availableQuantity')}</Typography>
                  <Typography variant="body2">{step.availableQuantity} {t('documents.pickingPath.unitPcs')}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('documents.pickingPath.location')}</Typography>
                  <Typography variant="body2">
                    {step.location.code}
                    {step.location.shelfLevel > 0 && (
                      <> ({t('documents.pickingPath.shelf')} {step.location.shelfLevel}, {step.location.shelfHeightCm}cm)</>
                    )}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('documents.pickingPath.availability')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: accessibilityColor,
                      }}
                    />
                    <Typography variant="body2">
                      {t(`documents.pickingPath.heightAccessibility.${step.heightAccessibility}`)}
                    </Typography>
                  </Box>
                </Box>
                {step.batchNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('documents.pickingPath.batch')}</Typography>
                    <Typography variant="body2">{step.batchNumber}</Typography>
                  </Box>
                )}
                {step.expiryDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('documents.pickingPath.expiryDate')}</Typography>
                    <Typography
                      variant="body2"
                      color={new Date(step.expiryDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) ? 'error' : 'inherit'}
                    >
                      {new Date(step.expiryDate).toLocaleDateString('pl-PL')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Collapse>
      </React.Fragment>
    );
  };

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          {t('documents.pickingPath.title')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('documents.pickingPath.strategy')}</InputLabel>
            <Select
              value={strategy}
              label={t('documents.pickingPath.strategy')}
              onChange={(e) => setStrategy(e.target.value as PickingStrategy)}
            >
              <MenuItem value="shortest">{t('documents.pickingPath.strategyShortest')}</MenuItem>
              <MenuItem value="shortest_3d">{t('documents.pickingPath.strategyShortest3d')}</MenuItem>
              <MenuItem value="fifo">{t('documents.pickingPath.strategyFifo')}</MenuItem>
              <MenuItem value="expiry_first">{t('documents.pickingPath.strategyExpiryFirst')}</MenuItem>
              <MenuItem value="rack_grouped">{t('documents.pickingPath.strategyRackGrouped')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t('documents.pickingPath.transportMode')}</InputLabel>
            <Select
              value={transportMode}
              label={t('documents.pickingPath.transportMode')}
              onChange={(e) => setTransportMode(e.target.value as TransportMode)}
            >
              <MenuItem value="forklift">{t('documents.pickingPath.transportForklift')}</MenuItem>
              <MenuItem value="pedestrian">{t('documents.pickingPath.transportPedestrian')}</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <StartIcon />}
            onClick={handleCalculatePath}
            disabled={loading || (!documentId && (!items || items.length === 0))}
          >
            {loading ? t('documents.pickingPath.calculating') : t('documents.pickingPath.calculatePath')}
          </Button>

          {pickingPath && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleCalculatePath}
              disabled={loading}
            >
              {t('documents.pickingPath.recalculate')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Path summary */}
      {pickingPath && (
        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ProductIcon color="primary" />
              <Typography variant="body2">
                <strong>{totalSteps}</strong> {t('documents.pickingPath.steps')}
              </Typography>
            </Box>
            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DistanceIcon color="primary" />
              <Typography variant="body2">
                <strong>{formatDistance(pickingPath.totalDistance)}</strong>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon color="primary" />
              <Typography variant="body2">
                ~<strong>{formatEstimatedTime(pickingPath.estimatedTimeSeconds)}</strong>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HeightIcon color="primary" />
              <Typography variant="body2">
                +<strong>{pickingPath.totalHeightPenalties.toFixed(0)}s</strong> ({t('documents.pickingPath.height')})
              </Typography>
            </Box> */}
          </Box>

          {/* Progress bar */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">
                {t('documents.pickingPath.progress')}: {completedCount}/{totalSteps}
              </Typography>
              <Typography variant="caption">
                {progress.toFixed(0)}%
              </Typography>
            </Box>
            <Box
              sx={{
                height: 8,
                bgcolor: 'grey.300',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${progress}%`,
                  bgcolor: progress === 100 ? 'success.main' : 'primary.main',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>

          {/* Show on map buttons */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="info"
              startIcon={<MapIcon />}
              size="small"
              fullWidth
              onClick={() => {
                const params = new URLSearchParams();
                if (documentId) params.set('document', String(documentId));
                params.set('warehouse', String(warehouseId));
                params.set('strategy', strategy);
                params.set('transport', transportMode);
                params.set('view', '2d');
                navigate(`/warehouse-map?${params.toString()}`);
              }}
            >
              {t('documents.pickingPath.showOnMap2d')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<View3DIcon />}
              size="small"
              fullWidth
              onClick={() => {
                const params = new URLSearchParams();
                if (documentId) params.set('document', String(documentId));
                params.set('warehouse', String(warehouseId));
                params.set('strategy', strategy);
                params.set('transport', transportMode);
                params.set('view', '3d');
                navigate(`/warehouse-map?${params.toString()}`);
              }}
            >
              {t('documents.pickingPath.showOnMap3d')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Unfulfilled items warning */}
      {pickingPath && !pickingPath.isComplete && pickingPath.unfulfilledItems.length > 0 && (
        <Alert severity="warning" sx={{ m: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {t('documents.pickingPath.notAllProductsCanBePicked')}
          </Typography>
          {pickingPath.unfulfilledItems.map((item) => (
            <Typography key={item.productId} variant="body2">
              • {item.productName} ({item.productCode}): {t('documents.pickingPath.missingQuantity', { count: item.quantityMissing })}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Steps list */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {!pickingPath && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('documents.pickingPath.selectStrategyAndCalculate')}
            </Typography>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {pickingPath && pickingPath.steps.length > 0 && (
          <List disablePadding>
            {pickingPath.steps.map(renderStepItem)}
          </List>
        )}

        {pickingPath && pickingPath.steps.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('documents.pickingPath.noProductsToPick')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer actions */}
      {pickingPath && completedCount === totalSteps && totalSteps > 0 && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'success.light' }}>
          <Typography variant="body1" fontWeight="bold" color="success.contrastText">
            {t('documents.pickingPath.allProductsPicked')}
          </Typography>
        </Box>
      )}

      {/* Rack Detail Dialog */}
      <RackDetailView
        open={rackDetailOpen}
        onClose={() => {
          setRackDetailOpen(false);
          setSelectedRackId(null);
          setHighlightedShelfLevel(undefined);
        }}
        locationId={selectedRackId}
        highlightedShelfLevel={highlightedShelfLevel}
      />
    </Paper>
  );
};

export default PickingPathView;
